import { Field, Formik } from 'formik';
import React, { forwardRef, useEffect, useState } from 'react';
import { InputField } from '../../../../../components/formFields/InputField';
import { SelectFormField } from '../../../../../components/formFields/SelectFormField';
import { apiService } from '../../../../../utils/api';
import * as Yup from 'yup';
import { Info12Regular } from '@fluentui/react-icons';

interface ProjectCreateFormProps {
  submit: (id: null, values: any) => void;
}

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Povinné pole'),
  name_official: Yup.string().required('Povinné pole'),
  identifier: Yup.string().required('Povinné pole'),
  etape: Yup.string().required('Povinné pole'),
  phase_type: Yup.string().required('Povinné pole'),
  building: Yup.string().required('Povinné pole'),
  building_object_title: Yup.string().required('Povinné pole'),
});

const ProjectCreateForm = forwardRef<any, ProjectCreateFormProps>(({ submit }, ref) => {
  const [buildings, setBuildings] = useState([]);
  const [phaseTypes, setPhaseTypes] = useState([]);
  const [etapes, setEtapes] = useState([]);

  useEffect(() => {
    const fetchPhaseTypes = async () => {
      try {
        const { data } = await apiService.get('/api/v1/phase-type/dropdown/?pagination=false');
        setPhaseTypes(data);
      } catch (e) {
        console.log(e);
      }
    };
    fetchPhaseTypes();
  }, []);

  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const { data } = await apiService.get('/api/v1/building/dropdown/?pagination=false');
        setBuildings(data);
      } catch (e) {
        console.log(e);
      }
    };
    fetchBuildings();
  }, []);

  return (
    <div>
      <Formik
        innerRef={ref}
        validationSchema={validationSchema}
        enableReinitialize
        initialValues={{
          title: '',
          name_official: '',
          identifier: '',
          etape: '',
          phase_type: '',
          building: '',
          building_object_title: '',
        }}
        onSubmit={(values) => {
          if (!values.building_object_title) {
            delete values.building_object_title;
          }
          submit(null, values);
        }}
      >
        {(props) => {
          const { values } = props;

          useEffect(() => {
            const fetchEtapes = async () => {
              try {
                const result = (
                  await apiService.get('/api/v1/etape/dropdown/', {
                    params: { building: values.building, pagination: 'false' },
                  })
                ).data;
                setEtapes(result);
              } catch (e) {
                console.log(e);
              }
            };

            if (values.building) {
              fetchEtapes();
            }
          }, [values.building]);

          return (
            <div className="space-y-1">
              <Field
                name="title"
                value={values.title}
                placeholder="Název"
                component={InputField}
                label="Název"
                required
                data-testid="project-create-title-input"
              />
              <Field
                name="name_official"
                value={values.name_official}
                placeholder="Oficiální název"
                component={InputField}
                label="Oficiální název"
                required
                data-testid="project-create-name-official-input"
              />
              <Field
                name="identifier"
                value={values.identifier}
                placeholder="identifier"
                component={InputField}
                label="Číslo akce"
                required
                data-testid="project-create-identifier-input"
              />
              <SelectFormField
                name="phase_type"
                placeholder=""
                data={phaseTypes || []}
                label="Typ faze"
                required
                data-testid="project-create-phase-type-select"
              />
              <div>
                <div className="flex items-center gap-1">
                  <span>Stavba</span>
                  <span title="Nemáte zde žadnou stavbu a nevíte jak na to? Návod: https://velehradsky-cakp.help.userguiding.com/cs/articles/9271-stavby">
                    <Info12Regular />
                  </span>
                </div>
                <SelectFormField
                  name="building"
                  placeholder=""
                  data={buildings || []}
                  label="Stavba"
                  required
                  data-testid="project-create-building-select"
                />
              </div>
              <SelectFormField
                name="etape"
                placeholder=""
                data={etapes}
                disabled={!values.building}
                label="Etapa"
                required
                data-testid="project-create-etape-select"
              />
              <Field
                name="building_object_title"
                value={values.building_object_title}
                placeholder="Stavební objekt"
                component={InputField}
                label="Stavební objekt"
                required
                data-testid="project-create-building-object-input"
              />
            </div>
          );
        }}
      </Formik>
    </div>
  );
});
ProjectCreateForm.displayName = 'ProjectCreateForm';

export default ProjectCreateForm;
